import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** URL to redirect to if not authenticated */
  redirectTo?: string;
  /** How long to wait for auth init before showing a timeout error (ms). Default 10 000. */
  authTimeoutMs?: number;
}

/**
 * Loading spinner component shown while auth is initialising.
 */
const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-indigo-600 border-r-transparent"></div>
      <p className="mt-4 text-gray-600">Authenticating...</p>
    </div>
  </div>
);

/**
 * Error UI shown when auth init has not resolved within the timeout window.
 */
const AuthTimeoutError: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center max-w-md px-6">
      <div className="mb-6">
        <svg
          className="mx-auto h-16 w-16 text-amber-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z"
          />
        </svg>
      </div>
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
        Authentication timed out
      </h2>
      <p className="text-gray-600 mb-6">
        Sign-in is taking longer than expected. Please check your connection and try again.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
      >
        Retry
      </button>
    </div>
  </div>
);

/**
 * Protected Route Component
 * 
 * Wraps routes that require authentication.
 * Redirects unauthenticated users to login.
 * Shows loading state while authentication is in progress.
 * 
 * @example
 * ```tsx
 * <Route 
 *   path="/dashboard" 
 *   element={
 *     <ProtectedRoute>
 *       <DashboardPage />
 *     </ProtectedRoute>
 *   } 
 * />
 * ```
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = '/login',
  authTimeoutMs = 10_000,
}) => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const location = useLocation();

  // Track whether the auth-init timeout has been reached
  const [authTimedOut, setAuthTimedOut] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isLoading) {
      // Auth resolved — clear any pending timeout
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setAuthTimedOut(false);
      return;
    }

    // Start the timeout when loading begins
    timerRef.current = setTimeout(() => {
      setAuthTimedOut(true);
    }, authTimeoutMs);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isLoading, authTimeoutMs]);

  // Show timeout error UI if auth init has not resolved in time
  if (authTimedOut) {
    return <AuthTimeoutError />;
  }

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // If not authenticated, redirect to login or trigger login
  if (!isAuthenticated) {
    // Store the attempted URL for redirecting after login
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    
    // If redirect is to '/login', we can auto-trigger login
    if (redirectTo === '/login') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md px-6">
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-indigo-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Authentication Required
            </h2>
            <p className="text-gray-600 mb-6">
              Please sign in to access this page.
            </p>
            <button
              onClick={() => login()}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      );
    }

    return <Navigate to={`${redirectTo}?returnUrl=${returnUrl}`} replace />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;

